export const PORTAL_MAPPING = {
  GST: {
    routes: ["/gstin", "/gstin/*"],
    displayName: "GST Portal",
  },
  "Income Tax": {
    routes: ["/income-tax", "/income-tax/*"],
    displayName: "Income Tax Portal",
  },
  TDS: {
    routes: ["/tds", "/tds/*"],
    displayName: "TDS/TCS Portal",
  },
};

export const getEnabledPortals = () => {
  try {
    const portals = localStorage.getItem("enabledPortals");
    return portals ? JSON.parse(portals) : [];
  } catch (error) {
    console.error("Error parsing enabled portals:", error);
    return [];
  }
};

export const getCurrentPlan = () => {
  return localStorage.getItem("currentPlan") || "Basic";
};

export const isPortalEnabled = (portalName) => {
  const enabledPortals = getEnabledPortals();
  return enabledPortals.includes(portalName);
};

export const checkRouteAccess = (pathname) => {
  const enabledPortals = getEnabledPortals();

  for (const [portalName, config] of Object.entries(PORTAL_MAPPING)) {
    const isRouteMatch = config.routes.some((route) => {
      if (route.endsWith("/*")) {
        const baseRoute = route.replace("/*", "");
        return pathname.startsWith(baseRoute);
      }
      return pathname === route || pathname.startsWith(route + "?");
    });

    if (isRouteMatch) {
      return {
        hasAccess: enabledPortals.includes(portalName),
        portalName: portalName,
        displayName: config.displayName,
        requiredUpgrade: !enabledPortals.includes(portalName),
      };
    }
  }

  return {
    hasAccess: true,
    portalName: null,
    displayName: null,
    requiredUpgrade: false,
  };
};

export const getAllPortalsWithStatus = () => {
  const enabledPortals = getEnabledPortals();

  return Object.entries(PORTAL_MAPPING).map(([portalName, config]) => ({
    name: portalName,
    displayName: config.displayName,
    routes: config.routes,
    isEnabled: enabledPortals.includes(portalName),
  }));
};

export const clearPortalAccess = () => {
  localStorage.removeItem("enabledPortals");
  localStorage.removeItem("currentPlan");
  localStorage.removeItem("subscriptionStatus");
  localStorage.removeItem("userVisiblePortals");
};

// ─── User-level portal access (Feature 2) ────────────────────────
//
// Org-level access lives in `enabledPortals` (driven by subscription
// plan). User-level access lives in `userVisiblePortals` and is the
// AND of (org-level enabled) AND (user-level enable_<portal>_portal).
// The BE returns `visible_portals` in /auth/login + /users/me — store
// it here so route guards / sidebar / dashboard tabs can read without
// extra fetches.
//
// Cross-org roles (super_admin, partner_admin) bypass: BE returns all
// 3 in `visible_portals` for those roles regardless of flags.

const USER_VISIBLE_PORTALS_KEY = "userVisiblePortals";

const PORTAL_KEY_TO_NAME = {
  income_tax: "Income Tax",
  gst: "GST",
  tds: "TDS",
};

const PORTAL_NAME_TO_KEY = {
  "Income Tax": "income_tax",
  "GST": "gst",
  "TDS": "tds",
};

/** Persist the BE-returned `visible_portals` array (snake_case keys
 *  like ["income_tax","gst"]). Called from the login response handler
 *  and after /users/me refreshes. */
export const setUserVisiblePortals = (portals) => {
  if (!Array.isArray(portals)) {
    localStorage.removeItem(USER_VISIBLE_PORTALS_KEY);
    return;
  }
  localStorage.setItem(USER_VISIBLE_PORTALS_KEY, JSON.stringify(portals));
};

/** Get the BE-determined effective list (org-AND-user). Returns an
 *  array of snake_case keys, e.g. ["income_tax", "gst"]. Empty array
 *  means "no portal access at all" — caller should hide everything. */
export const getUserVisiblePortals = () => {
  try {
    const raw = localStorage.getItem(USER_VISIBLE_PORTALS_KEY);
    if (!raw) return null;  // null = "not loaded yet"; [] = "loaded, empty"
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Boolean check: does the current user have access to a portal? Accepts
 *  either the snake_case key ("gst") or the display name ("GST"). */
export const userHasPortalAccess = (portalKeyOrName) => {
  const visible = getUserVisiblePortals();
  if (!visible) return true;  // pre-load: don't blank out the UI on first paint
  const key = PORTAL_NAME_TO_KEY[portalKeyOrName] || portalKeyOrName;
  return visible.includes(key);
};

/** Combined: org-level AND user-level. The other helpers in this file
 *  cover org-level only — call this from route guards / menus. Accepts
 *  the display name ("GST") or snake key. */
export const userHasFullPortalAccess = (portalKeyOrName) => {
  const displayName =
    PORTAL_KEY_TO_NAME[portalKeyOrName] || portalKeyOrName;
  return isPortalEnabled(displayName) && userHasPortalAccess(portalKeyOrName);
};

/** Route → portal mapping convenience (companion to checkRouteAccess
 *  which already handles org-level). Returns null if pathname isn't
 *  per-portal scoped, else the display name. */
export const portalForRoute = (pathname) => {
  for (const [portalName, config] of Object.entries(PORTAL_MAPPING)) {
    const match = config.routes.some((route) => {
      if (route.endsWith("/*")) {
        const base = route.replace("/*", "");
        return pathname.startsWith(base);
      }
      return pathname === route || pathname.startsWith(route + "?");
    });
    if (match) return portalName;
  }
  return null;
};

/**
 * Get the full subscription status object from localStorage
 * @returns {Object} Subscription status object with all details
 */
export const getSubscriptionStatus = () => {
  try {
    const status = localStorage.getItem("subscriptionStatus");
    if (status) {
      return JSON.parse(status);
    }
  } catch (error) {
    console.error("Error parsing subscription status:", error);
  }
  // Default trial status
  return {
    status: "Free Trial",
    display_status: "Free Trial",
    is_trial: true,
    is_admin: false,
    trial_days_remaining: 7,
    subscription_end_date: null,
    subscription_category: null,
    plan_name: null,
    plan_code: null,
    portals: [
      { name: "Income Tax", enabled: true, category: "Trial" },
      { name: "GST", enabled: true, category: "Trial" },
      { name: "TDS", enabled: true, category: "Trial" },
    ],
  };
};

/**
 * Get display-friendly subscription status text
 * @returns {string} Display text for the subscription badge
 */
export const getSubscriptionDisplayStatus = () => {
  const status = getSubscriptionStatus();
  return status.display_status || status.status || "Free Trial";
};

/**
 * Check if user is on free trial
 * @returns {boolean}
 */
export const isOnTrial = () => {
  const status = getSubscriptionStatus();
  return status.is_trial === true;
};

/**
 * Check if subscription/trial is expired
 * @returns {boolean}
 */
export const isSubscriptionExpired = () => {
  const status = getSubscriptionStatus();
  return status.is_expired === true;
};

/**
 * Get subscription category (Essential/Advanced)
 * @returns {string|null}
 */
export const getSubscriptionCategory = () => {
  const status = getSubscriptionStatus();
  return status.subscription_category;
};

/**
 * Get trial days remaining
 * @returns {number}
 */
export const getTrialDaysRemaining = () => {
  const status = getSubscriptionStatus();
  return status.trial_days_remaining || 0;
};

/**
 * Get subscription end date
 * @returns {string|null}
 */
export const getSubscriptionEndDate = () => {
  const status = getSubscriptionStatus();
  return status.subscription_end_date || status.trial_end_date;
};

/**
 * Get portal-specific subscription details
 * @param {string} portalName - "Income Tax", "GST", or "TDS"
 * @returns {Object|null}
 */
export const getPortalSubscriptionDetails = (portalName) => {
  const status = getSubscriptionStatus();
  if (status.portals && Array.isArray(status.portals)) {
    return status.portals.find((p) => p.name === portalName) || null;
  }
  return null;
};
